import tacticalLogo from '@/assets/tactical-tax-logo.png';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { reactivateSelf, SELF_REACTIVATE_DAYS } from '@/lib/deactivation';
import { sanitizeError } from '@/lib/errorMessages';

const STATUS_MESSAGES: Record<string, { title: string; body: string }> = {
  suspended: {
    title: 'Account Suspended',
    body: 'Your account has been suspended. Contact support to resolve this.',
  },
  trial_expired: {
    title: 'Trial Period Ended',
    body: 'Your trial has ended. Contact support to activate your subscription and regain access.',
  },
  past_due: {
    title: 'Payment Past Due',
    body: 'Your last payment failed. Please update your payment method to restore access.',
  },
  canceled: {
    title: 'Subscription Canceled',
    body: 'Your subscription has been canceled. Resubscribe to regain access.',
  },
  deactivated: {
    title: 'Account Deactivated',
    body: `This account has been deactivated. If you deactivated it yourself within the last ${SELF_REACTIVATE_DAYS} days, you can bring it back right here; otherwise contact support.`,
  },
  inactive: {
    title: 'No Active Subscription',
    body: "You don't have an active subscription. Please subscribe to access Tactical Tax Genie.",
  },
};

interface Props {
  reason?: string;
}

export default function SubscriptionLocked({ reason }: Props) {
  const { org, signOut, refreshUserData } = useAuth();
  const [reactivating, setReactivating] = useState(false);

  // reactivate_self() restores a self-deactivated account (and the org it took
  // with it) inside the window, and answers false for an admin-deactivated
  // account or one past the window; the profile refresh is what lets them in.
  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const ok = await reactivateSelf();
      if (ok) {
        toast.success('Welcome back', { description: 'Your account is active again.' });
        await refreshUserData();
      } else {
        toast.error("This account can't be reactivated from here", {
          description: `It was deactivated by an administrator, or more than ${SELF_REACTIVATE_DAYS} days ago. Contact support.`,
        });
      }
    } catch (err) {
      toast.error('Reactivation failed', { description: sanitizeError(err) });
    } finally {
      setReactivating(false);
    }
  };

  // Priority: explicit reason prop → org subscription status → fallback
  const key = reason ?? org?.subscription_status ?? 'inactive';
  const msg = STATUS_MESSAGES[key] ?? STATUS_MESSAGES.inactive;

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-8 px-6">
      <img src={tacticalLogo} alt="Tactical Tax Genie" className="h-16 w-16 object-contain" style={{ borderRadius: 0 }} />

      <div className="max-w-md w-full text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-red-500">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm font-semibold uppercase tracking-wide">Access Restricted</span>
        </div>
        <h1 className="text-2xl font-bold" style={{ color: '#2D4B6E' }}>{msg.title}</h1>
        <p className="text-gray-500 text-sm">{msg.body}</p>
      </div>

      <div className="flex flex-col items-center gap-3 max-w-sm w-full">
        {key === 'deactivated' && (
          <Button className="w-full" onClick={handleReactivate} disabled={reactivating}>
            {reactivating && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Reactivate my account
          </Button>
        )}
        <a
          href="mailto:support@tacticaltaxgenie.com"
          className="w-full"
        >
          <Button className="w-full" style={{ backgroundColor: '#2196F3' }}>
            Contact Support
          </Button>
        </a>
        <Button variant="ghost" className="w-full text-muted-foreground" onClick={signOut}>
          Sign Out
        </Button>
      </div>

      <p className="text-xs text-gray-300">© 2026 Tactical Tax Genie. All rights reserved.</p>
    </div>
  );
}
